import { Trans, useLingui } from '@lingui/react/macro';

import { Label } from '@/design_system/Label/Label';
import Message from '@/design_system/Message';
import { RadioItem, RadioItemGroup } from '@/design_system/Radio/RadioItem/RadioItem';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import { useUpdateArticleExchange } from '@/models/articleExchange';
import { getAnalysisExchangeTypes } from '@/models/workflow';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useGetExchangeTypes } from '@/utils/exchangeTypes';

export const ExchangeTypeInput = () => {
  const { t } = useLingui();
  const { article, errors } = useArticleContext();
  const { workflow } = useRequestContext();
  const { mutate: updateArticleExchange } = useUpdateArticleExchange(article.id);
  const hasMissingExchangeTypeError = errors.services?.exchange?.missingExchangeType;
  const { exchangeTypeOptions } = useGetExchangeTypes();
  const analysisExchangeTypes = getAnalysisExchangeTypes({ workflow, article });

  const availableExchangeTypes = exchangeTypeOptions.filter((option) =>
    analysisExchangeTypes?.includes(option.value)
  );

  return (
    !!availableExchangeTypes.length && (
      <Stack gap="0.25rem">
        <Label
          label={t({
            id: 'service-choice.exchange.exchange-type.label',
            message: 'Exchange Type',
          })}
        />
        <RadioItemGroup
          isInvalid={hasMissingExchangeTypeError}
          name="default"
          value={article.snapshot.articleExchange?.type}
          onChange={(value) => updateArticleExchange({ type: value })}
        >
          {availableExchangeTypes.map((option) => (
            <RadioItem key={option.value} ariaLabel={option.label} value={option.value}>
              {option.label}
            </RadioItem>
          ))}
        </RadioItemGroup>

        {hasMissingExchangeTypeError && (
          <Message type="error">
            <Trans id="exchange-type.errors.missing">Please select an exchange type</Trans>
          </Message>
        )}
      </Stack>
    )
  );
};

export const ExchangeType = () => {
  const { article } = useArticleContext();
  const { exchangeTypeOptions } = useGetExchangeTypes();
  const { t } = useLingui();

  return (
    <p>
      <InlineText color="secondary">
        • <Trans id="service-choice.exchange.service-type.label">Service type:</Trans>{' '}
      </InlineText>
      <InlineText weight="medium">
        {exchangeTypeOptions.find(
          (option) => option.value === article.snapshot.articleExchange?.type
        )?.label ||
          t({
            id: 'service-choice.radio.exchange.label',
            message: 'Exchange',
          })}
      </InlineText>
    </p>
  );
};
